import React, { useState } from 'react';
import { FullFieldContext } from './FieldEditor';
import { FieldContextOverrides } from '../../../utils/faqApi';

// ---------------------------------------------------------------------------
// Code generation
// ---------------------------------------------------------------------------

function toTsxValue(value: unknown, indent: string): string {
  if (typeof value === 'boolean') return `{${value}}`;
  if (typeof value === 'string') return `"${value.replace(/"/g, '\\"')}"`;
  if (Array.isArray(value)) {
    if (value.length === 0) return '{[]}';
    const items = value
      .map((v) => `${indent}    "${String(v).replace(/"/g, '\\"')}"`)
      .join(',\n');
    return `{[\n${items},\n${indent}  ]}`;
  }
  return `{${JSON.stringify(value)}}`;
}

interface SnippetParts {
  hasOverrides: boolean;
  lines: Array<{ type: 'comment' | 'tag-open' | 'prop' | 'tag-close' | 'blank'; text: string }>;
}

function buildSnippet(
  topic: string,
  canonical: FullFieldContext,
  edited: FullFieldContext
): SnippetParts {
  const delta: Partial<FieldContextOverrides> = {};
  (Object.keys(edited) as Array<keyof FullFieldContext>).forEach((key) => {
    if (JSON.stringify(canonical[key]) !== JSON.stringify(edited[key])) {
      (delta as any)[key] = edited[key];
    }
  });

  const hasOverrides = Object.keys(delta).length > 0;

  const lines: SnippetParts['lines'] = [];

  if (hasOverrides) {
    lines.push({ type: 'comment', text: '{/* DEV_OVERRIDE: remove once overrides are promoted to lambda_function_dual.py */}' });
  } else {
    lines.push({ type: 'comment', text: '{/* Production usage — field context is managed by the Lambda */}' });
  }

  lines.push({ type: 'tag-open', text: '<FAQAssist' });
  lines.push({ type: 'prop', text: `  topic="${topic}"` });

  if (hasOverrides) {
    // Build fieldContextOverrides prop
    const overrideEntries = Object.entries(delta);
    // Opening
    lines.push({ type: 'prop', text: '  fieldContextOverrides={{' });
    overrideEntries.forEach(([key, value]) => {
      const tsxVal = toTsxValue(value, '  ');
      // Multi-line arrays: split and emit each line as its own entry
      if (Array.isArray(value) && value.length > 0) {
        lines.push({ type: 'prop', text: `    ${key}: [` });
        (value as string[]).forEach((item, i) => {
          const comma = i < value.length - 1 ? ',' : '';
          lines.push({ type: 'prop', text: `      "${String(item).replace(/"/g, '\\"')}"${comma}` });
        });
        lines.push({ type: 'prop', text: '    ],' });
      } else if (typeof value === 'boolean') {
        lines.push({ type: 'prop', text: `    ${key}: ${value},` });
      } else if (typeof value === 'string') {
        lines.push({ type: 'prop', text: `    ${key}: "${value.replace(/"/g, '\\"')}",` });
      } else {
        lines.push({ type: 'prop', text: `    ${key}: ${tsxVal},` });
      }
    });
    lines.push({ type: 'prop', text: '  }}' });
    lines.push({ type: 'prop', text: '  debugConfig={true}' });
  }

  lines.push({ type: 'tag-close', text: '/>' });

  return { hasOverrides, lines };
}

// ---------------------------------------------------------------------------
// Syntax coloring helpers
// Return a ReactNode representing a colored line
// ---------------------------------------------------------------------------

// Color palette (Dracula-ish, works on dark bg):
//   JSX tags:      text-blue-300
//   prop names:    text-amber-300
//   string values: text-green-300
//   boolean/num:   text-purple-300
//   brackets {}[]: text-gray-300
//   comments:      text-gray-500 italic

function colorLine(line: SnippetParts['lines'][0]): React.ReactNode {
  const text = line.text;

  if (line.type === 'comment') {
    return <span className="text-gray-500 italic">{text}</span>;
  }

  if (line.type === 'tag-open') {
    return <span className="text-blue-300">{text}</span>;
  }

  if (line.type === 'tag-close') {
    return <span className="text-blue-300">{text}</span>;
  }

  if (line.type === 'blank') {
    return <span>&nbsp;</span>;
  }

  // prop lines — parse key: value patterns
  // Cases: `  topic="..."`, `  fieldContextOverrides={{`, `    key: value,`, etc.
  const kvMatch = text.match(/^(\s*)(\w+)(=|\s*:\s*)(.*)$/);
  if (kvMatch) {
    const [, ws, key, sep, rest] = kvMatch;

    // Determine rest color
    let restEl: React.ReactNode;
    if (rest === '{{' || rest === '}}' || rest === '[' || rest === '],' || rest === '}}' || rest.trim() === '') {
      restEl = <span className="text-gray-200">{rest}</span>;
    } else if (rest.startsWith('"') || (rest.startsWith('"') && rest.endsWith('",'))) {
      restEl = <span className="text-green-300">{rest}</span>;
    } else if (rest === 'true,' || rest === 'false,' || rest === 'true' || rest === 'false' || rest === '{true}' || rest === '{false}') {
      restEl = <span className="text-purple-300">{rest}</span>;
    } else if (rest.startsWith('[') || rest.startsWith('{[')) {
      restEl = <span className="text-gray-200">{rest}</span>;
    } else {
      restEl = <span className="text-green-300">{rest}</span>;
    }

    return (
      <>
        <span className="text-gray-400">{ws}</span>
        <span className="text-amber-300">{key}</span>
        <span className="text-gray-400">{sep}</span>
        {restEl}
      </>
    );
  }

  // Indented string items like `      "some text",`
  const itemMatch = text.match(/^(\s+)(".*")(,?)$/);
  if (itemMatch) {
    const [, ws, str, comma] = itemMatch;
    return (
      <>
        <span className="text-gray-400">{ws}</span>
        <span className="text-green-300">{str}</span>
        <span className="text-gray-300">{comma}</span>
      </>
    );
  }

  // Closing brackets / braces
  if (/^\s*[\]\}],?\s*$/.test(text) || text.trim() === '}}') {
    return <span className="text-gray-200">{text}</span>;
  }

  return <span className="text-gray-200">{text}</span>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ReactSnippetPanelProps {
  topic: string;
  canonical: FullFieldContext | null;
  edited: FullFieldContext | null;
}

const ReactSnippetPanel: React.FC<ReactSnippetPanelProps> = ({ topic, canonical, edited }) => {
  const [copied, setCopied] = useState(false);

  if (!canonical || !edited) {
    return (
      <div className="text-sm text-gray-400 italic py-4 text-center">
        Select a topic to see the component snippet.
      </div>
    );
  }

  const { hasOverrides, lines } = buildSnippet(topic, canonical, edited);

  // Plain-text version for clipboard
  const plainText = lines.map((l) => l.text).join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
    } catch {
      const el = document.createElement('textarea');
      el.value = plainText;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">React Component Usage</span>
          {hasOverrides ? (
            <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-medium">
              dev overrides
            </span>
          ) : (
            <span className="text-xs bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded font-medium">
              production
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className={`text-xs px-3 py-1 rounded border transition-colors ${
            copied
              ? 'bg-green-50 border-green-300 text-green-700'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>

      {/* Code block */}
      <div className="bg-gray-900 rounded overflow-x-auto">
        {/* File tab label */}
        <div className="flex items-center gap-1 px-4 pt-2 pb-1 border-b border-gray-700">
          <span className="text-xs text-gray-400 font-mono">MyFormSection.tsx</span>
        </div>
        <pre className="font-mono text-xs leading-relaxed p-4 select-text">
          {lines.map((line, i) => (
            <div key={i}>{colorLine(line)}</div>
          ))}
        </pre>
      </div>

      <p className="mt-2 text-xs text-gray-500">
        {hasOverrides
          ? 'Copy this snippet to your component during development. Once the overrides are promoted to the Lambda config, replace it with the clean production form above.'
          : 'This is the clean production form. No client-side field context configuration required.'}
      </p>
    </div>
  );
};

export default ReactSnippetPanel;
